/**
 * Utility to parse and validate window.__APP_DATA__ for server-side rendered initial data
 */

export interface AppData {
  apiUrl?: string;
  screen: string;
  data: {
    variable_data?: any;
    parameters?: any;
    mode?: string;
    config?: {
      basic?: string;
      variables?: string;
      classes?: JSON;
      urls?: {
        [key: string]: string;
      };
      style?: any;
    };
    screen?: {
      id: string;
      sections: any[];
      settings?: {
        script?: string;
        style?: {
          background?: any[];
        };
        variables?: Record<string, any>;
        baseScreenResolution?: string;
        page?: {
          description?: string;
          title?: string;
        };
        skeletonCache?: boolean;
      };
    };
    live_updates?: boolean;
  };
}

/**
 * Check if window.__APP_DATA__ exists and is valid
 */
export const hasInitialData = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof (window as any).__APP_DATA__ !== 'undefined' &&
         (window as any).__APP_DATA__ !== null;
};

/**
 * Parse and validate window.__APP_DATA__
 */
export const parseInitialData = (): AppData | null => {
  if (!hasInitialData()) {
    return null;
  }

  try {
    const appData = (window as any).__APP_DATA__ as AppData;
    
    // Validate required fields
    if (!appData.screen || !appData.data) {
      console.warn('Invalid __APP_DATA__: missing required fields');
      return null;
    }

    return appData;
  } catch (error) {
    console.error('Failed to parse __APP_DATA__:', error);
    return null;
  }
};

/**
 * Transform initial data to match the page state structure
 */
export const transformInitialDataToPageState = (appData: AppData) => {
  const { data } = appData;
  const screen = data.screen;
  const enhancedSettings = {
    ...screen?.settings,
    live_updates: data.live_updates || false,
  };

  if (!screen) {
    return null;
  }

  return {
    screenId: appData.screen,
    data: screen.sections || [],
    settings: enhancedSettings,
    variableData: data.variable_data || {},
    mode: data.mode,
  };
};

/**
 * Transform initial data to match the config state structure
 */
export const transformInitialDataToConfigState = (appData: AppData) => {
  const { data } = appData;
  
  if (!data.config) {
    return null;
  }

  return {
    data: data.config,
  };
};

/**
 * Remove the initialization script tag from DOM after data is parsed
 */
export const removeInitScript = (): void => {
  if (typeof document !== 'undefined') {
    const initScript = document.getElementById('init');
    if (initScript && initScript.parentNode) {
      initScript.parentNode.removeChild(initScript);
      console.log('Initial data script removed from DOM');
    }
  }
};

/**
 * Clear window.__APP_DATA__ after it's been consumed
 */
export const clearInitialData = (): void => {
  if (typeof window !== 'undefined') {
    delete (window as any).__APP_DATA__;
  }
};
